import {graphql, useFragment, useRelayEnvironment} from 'react-relay'
import {useCallback, useState} from 'react'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {ActionList, Box, Text} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import type {IssueTypesDialogIssueTypes$key} from './__generated__/IssueTypesDialogIssueTypes.graphql'
import type {IssueTypesDialogIssueType$key} from './__generated__/IssueTypesDialogIssueType.graphql'
import {commitUpdateIssueIssueTypeMutation} from '../../mutations/update-issue-issue-type-mutation'
import {LABELS} from '../../constants/labels'
import {ERRORS} from '../../constants/errors'
import {BUTTON_LABELS} from '../../constants/buttons'
import {useAnalytics} from '@github-ui/use-analytics'
import {IssueTypeEntry} from './IssueTypeEntry'

const UNSET_ID = 'UNSET'

const IssueTypeFragment = graphql`
  fragment IssueTypesDialogIssueType on IssueType {
    id
  }
`

export const IssueTypesFragment = graphql`
  fragment IssueTypesDialogIssueTypes on IssueTypeConnection {
    edges {
      node {
        isEnabled
        ...IssueTypeEntry
      }
    }
  }
`

export type IssueTypesDialogProps = {
  onClose: () => void
  issueId: string
  issueTypes: IssueTypesDialogIssueTypes$key
  initialSelectedType?: IssueTypesDialogIssueType$key
  returnFocusRef?: React.RefObject<HTMLLIElement>
}

export const IssueTypesDialog = ({
  onClose,
  issueId,
  issueTypes,
  initialSelectedType,
  returnFocusRef,
}: IssueTypesDialogProps) => {
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()
  const {sendAnalyticsEvent} = useAnalytics()

  const preloadedIssueTypes = useFragment(IssueTypesFragment, issueTypes)
  const selectedIssueType = useFragment<IssueTypesDialogIssueType$key>(IssueTypeFragment, initialSelectedType ?? null)

  const enabledIssueTypes = (preloadedIssueTypes?.edges || []).flatMap(issueType => {
    if (!issueType?.node) return []

    return issueType?.node.isEnabled ? [issueType.node] : []
  })

  const [selectedTypeId, setSelectedTypeId] = useState<string>(selectedIssueType?.id || UNSET_ID)

  const handleCloseDialog = useCallback(() => {
    onClose()
    setTimeout(() => {
      returnFocusRef?.current?.focus()
    }, 100)
  }, [onClose, returnFocusRef])

  const handleSelect = useCallback(() => {
    const issueTypeId = selectedTypeId === UNSET_ID ? null : selectedTypeId
    sendAnalyticsEvent('issue_viewer.update_issue_type', 'ISSUE_VIEWER_UPDATE_ISSUE_TYPE_DIALOG', {
      initialIssueTypeId: selectedIssueType?.id ?? '',
      issueTypeId: issueTypeId ?? '',
    })

    commitUpdateIssueIssueTypeMutation({
      environment,
      input: {issueId, issueTypeId},
      onError: (e: Error) => {
        handleCloseDialog()
        // eslint-disable-next-line compat/compat
        reportError(formatError(e.message))
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({type: 'error', message: ERRORS.issueTypeUpdateError})
      },
      onCompleted: handleCloseDialog,
    })
  }, [selectedTypeId, sendAnalyticsEvent, selectedIssueType?.id, environment, issueId, handleCloseDialog, addToast])

  return (
    <Dialog
      title={LABELS.issueTypes.issueTypeDialogHeader}
      onClose={handleCloseDialog}
      sx={{width: 'var(--overlay-width-medium, 480px)'}}
      renderBody={() => (
        <Box sx={{maxHeight: 'var(--overlay-height-large, 432px)'}}>
          <ActionList selectionVariant="single" role="menu" aria-label="Issue Type selection" showDividers>
            <ActionList.Item
              role="menuitemradio"
              selected={selectedTypeId === UNSET_ID}
              aria-checked={selectedTypeId === UNSET_ID}
              onSelect={() => setSelectedTypeId(UNSET_ID)}
              sx={{py: 2}} // this .item is without a description, this balance the visuals.
            >
              <Text sx={{fontWeight: 'bold'}}>{LABELS.issueTypes.noIssueTypeOptionName}</Text>
            </ActionList.Item>
            {enabledIssueTypes.map((issueType, index) => (
              <IssueTypeEntry
                key={index}
                issueType={issueType}
                selectedTypeId={selectedTypeId}
                setSelectedTypeId={setSelectedTypeId}
              />
            ))}
          </ActionList>
        </Box>
      )}
      footerButtons={[
        {buttonType: 'default', content: BUTTON_LABELS.cancel, onClick: handleCloseDialog},
        {
          buttonType: 'primary',
          content: BUTTON_LABELS.changeType,
          onClick: handleSelect,
        },
      ]}
    />
  )
}

const formatError = (message: string) => new Error(ERRORS.issueTypeUpdateErrorReporting(message))

try{ IssueTypesDialog.displayName ||= 'IssueTypesDialog' } catch {}